import {
  permissionQueryClient,
  sftpUsersKeys,
  useCreateSftpUserMutation,
  useUpdateSftpUserMutation,
} from '@data-delivery/permission-ui-data-access-state';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { showErrorToast } from '../../../utils/error-utils';
import type { UserFormValues } from '../schemas/user-form.schema';

interface UseUserMutationOptions {
  mode: 'create' | 'edit';
  userId?: string;
  lastModified?: number;
  businessGroupId?: string;
  requireAssignments: boolean;
  onSuccess?: () => void;
}

function parseDelimitedList(raw: string): string[] {
  return raw
    .split(/[\n]/)
    .flatMap(s => s.split(','))
    .map(s => s.trim())
    .filter(Boolean);
}

function buildCreatePayload(values: UserFormValues, requireAssignments: boolean) {
  const ipList = values.ipWhitelist ? parseDelimitedList(values.ipWhitelist) : [];
  const payload = {
    loginId: values.loginId,
    distributionList: values.distributionList ? parseDelimitedList(values.distributionList) : [],
    ipWhitelist: ipList.length > 0 ? ipList : ['0.0.0.0/0'],
    landingFolder: values.landingFolder,
    sfAccountId: values.sfAccountId,
    contentAccessRole: values.contentAccessRole,
    orgName: values.orgName,
    rootFolder: values.rootFolder,
    serviceAccountOwner: values.serviceAccountOwner,
    assignments: requireAssignments
      ? values.assignments
      : values.assignments && values.assignments.length > 0
      ? values.assignments
      : undefined,
    firstName: values.firstName,
    lastName: values.lastName,
    isSystemUser: values.isSystemUser,
    isLoginAdmin: values.isLoginAdmin,
    isEnabled: values.isEnabled,
  };

  return Object.fromEntries(
    Object.entries(payload).filter(([, v]) => v !== undefined),
  ) as typeof payload;
}

function buildUpdatePayload(
  values: UserFormValues,
  requireAssignments: boolean,
  lastModified: number,
) {
  const ipList = values.ipWhitelist ? parseDelimitedList(values.ipWhitelist) : [];
  const payload = {
    distributionList: values.distributionList ? parseDelimitedList(values.distributionList) : [],
    ipWhitelist: ipList.length > 0 ? ipList : ['0.0.0.0/0'],
    landingFolder: values.landingFolder,
    sfAccountId: values.sfAccountId,
    contentAccessRole: values.contentAccessRole,
    orgName: values.orgName,
    rootFolder: values.rootFolder,
    serviceAccountOwner: values.serviceAccountOwner,
    lastModified,
    assignments: requireAssignments
      ? values.assignments
      : values.assignments && values.assignments.length > 0
      ? values.assignments
      : undefined,
    firstName: values.firstName,
    lastName: values.lastName,
    isSystemUser: values.isSystemUser,
    isLoginAdmin: values.isLoginAdmin,
    isEnabled: values.isEnabled,
  };

  return Object.fromEntries(
    Object.entries(payload).filter(([, v]) => v !== undefined),
  ) as typeof payload;
}

export function useUserMutation({
  mode,
  userId,
  lastModified,
  businessGroupId,
  requireAssignments,
  onSuccess,
}: UseUserMutationOptions) {
  const queryClient = useQueryClient();
  const createMutation = useCreateSftpUserMutation();
  const updateMutation = useUpdateSftpUserMutation();

  const invalidateQueries = () => {
    if (businessGroupId) {
      queryClient.invalidateQueries({
        queryKey: ['business-group-users', businessGroupId],
      });
    }

    if (!requireAssignments) {
      permissionQueryClient.invalidateQueries({
        queryKey: sftpUsersKeys.visible().queryKey,
      });
    }
  };

  const handleSuccess = () => {
    invalidateQueries();
    toast.success(`User ${mode === 'create' ? 'created' : 'updated'} successfully`);
    onSuccess?.();
  };

  const handleError = (error: unknown) => {
    showErrorToast(error);
  };

  const submit = (values: UserFormValues) => {
    if (mode === 'create') {
      const payload = buildCreatePayload(values, requireAssignments);
      createMutation.mutate(payload, {
        onSuccess: handleSuccess,
        onError: handleError,
      });
    } else if (userId) {
      const changes = buildUpdatePayload(values, requireAssignments, lastModified ?? Date.now());
      updateMutation.mutate(
        { id: userId, changes },
        {
          onSuccess: handleSuccess,
          onError: handleError,
        },
      );
    }
  };

  return {
    submit,
    isPending: createMutation.isPending || updateMutation.isPending,
  };
}
